import React from "react";
import { Helmet } from "react-helmet-async";

const DEFAULT_SITE_NAME = "Yoga Retreats Booking";
const DEFAULT_BASE_URL = "https://yogaretreatsbooking.com";

// SEO limits: keep the <title> within ~60 chars and meta description within
// ~160 chars so SEMrush / Google don't flag them as too long / truncated.
const MAX_TITLE_LENGTH = 60;
const MAX_DESCRIPTION_LENGTH = 160;

function toAbsoluteUrl(url, baseUrl) {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (!url.startsWith("/")) return `${baseUrl}/${url}`;
  return `${baseUrl}${url}`;
}

function clamp(text, max) {
  if (!text) return text;
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return clean.slice(0, max - 1).trimEnd() + "\u2026";
}

/**
 * Build a title <= MAX_TITLE_LENGTH. Append the brand name only when it still
 * fits; otherwise drop the brand (and finally clamp the title itself).
 */
function buildTitle(title, siteName, appendSiteName) {
  if (!title) return clamp(siteName, MAX_TITLE_LENGTH);
  if (appendSiteName) {
    const withBrand = `${title} | ${siteName}`;
    if (withBrand.length <= MAX_TITLE_LENGTH) return withBrand;
  }
  return clamp(title, MAX_TITLE_LENGTH);
}

function Seo({
  title,
  description,
  canonicalPath = "/",
  ogImagePath = "/favicon.ico",
  baseUrl = DEFAULT_BASE_URL,
  siteName = DEFAULT_SITE_NAME,
  robots = "index,follow",
  appendSiteName = true,
  jsonLd,
}) {
  const fullTitle = buildTitle(title, siteName, appendSiteName);
  const metaDescription = clamp(description, MAX_DESCRIPTION_LENGTH);
  const canonicalUrl = toAbsoluteUrl(canonicalPath, baseUrl);
  const ogImageUrl = toAbsoluteUrl(ogImagePath, baseUrl);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {metaDescription ? <meta name="description" content={metaDescription} /> : null}
      {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}
      <link rel="sitemap" type="application/xml" title="Sitemap" href={`${baseUrl}/sitemap.xml`} />
      <meta name="robots" content={robots} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      {fullTitle ? <meta property="og:title" content={fullTitle} /> : null}
      {metaDescription ? <meta property="og:description" content={metaDescription} /> : null}
      {canonicalUrl ? <meta property="og:url" content={canonicalUrl} /> : null}
      {ogImageUrl ? <meta property="og:image" content={ogImageUrl} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      {fullTitle ? <meta name="twitter:title" content={fullTitle} /> : null}
      {metaDescription ? <meta name="twitter:description" content={metaDescription} /> : null}
      {ogImageUrl ? <meta name="twitter:image" content={ogImageUrl} /> : null}

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}

export default Seo;
